import type { FC, ReactNode } from 'react';
import { QFlex, QSpacer } from '../../QLayouts';
import { QButton } from '../../QAtoms';
import { partitionChildren } from '../../utils/partitionChildren';
import {
  QReorderButtons,
  QReorderButtonsProps,
} from '../ReorderButtons/ReorderButtons';

interface CardFooterExport extends FC<{ children?: ReactNode }> {
  Simple: FC<QSimpleCardFooterProps>;
}

/**
 * Renders a footer for a card.
 * Expects parent to provide outer border.
 *
 * For a simple footer with delete and/or reorder buttons,
 * use the `QCardFooter.Simple` variant.
 */
export const QCardFooter: CardFooterExport = ({ children }) => {
  const { QButton, unmatched } = partitionChildren(children, 'QButton');

  return (
    <QFlex
      height="48px"
      align="center"
      padding="8px"
      borderTop="1px solid"
      borderColor="gray.200"
      background="gray.50"
    >
      {QButton}
      <QSpacer />
      {unmatched}
    </QFlex>
  );
};
QCardFooter.displayName = 'QCardFooter';

export type QSimpleCardFooterProps = QReorderButtonsProps & {
  onDelete?: () => void;
};

const Simple: FC<QSimpleCardFooterProps> = ({
  onDelete,
  ...reorderButtonsProps
}) => (
  <QCardFooter>
    {onDelete && (
      <QButton
        isDestructive
        leftIcon="Trash"
        onClick={onDelete}
        variant="ghost"
      >
        Delete
      </QButton>
    )}
    <QReorderButtons {...reorderButtonsProps} />
  </QCardFooter>
);
Simple.displayName = 'QCardFooter';

QCardFooter.Simple = Simple;
